import { Component, OnDestroy, OnInit, Output, EventEmitter, ChangeDetectorRef, ChangeDetectionStrategy, HostListener, ElementRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, finalize, forkJoin, of, Subscription } from 'rxjs';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import {
  ExpenseTransAddEditService,
  LevelOption,
  ParentMeOption,
  GrossIncomeOption,
  MapOption,
  MethodOption,
  SystemAccountOption,
  LocalAccountOption,
  ScheduleMOption,
  BaseCodeOption,
  AttribMastOption,
  TransCategoryRowData,
  SaveTransCategoryPayload
} from './expense-trans-add-edit.service';

// ============================================
// FORM STATE INTERFACE
// ============================================
interface FormState {
  // Mode
  isEditMode: boolean;
  isCreate: boolean;

  // Loading states
  isLoading: boolean;
  isLoadingMapDetails: boolean;
  isSaveClicked: boolean;
  crudLoading: boolean;

  // Visibility flags (controlled by state)
  level1Show: boolean;
  level2Show: boolean;
  showMethodDesc: boolean;
  showBaseFdii: boolean;
  showBaseBasket: boolean;
  showBaseType: boolean;
  showBaseLevel: boolean;
  showBaseEntityLevel: boolean;
  showBaseFdiiSingle: boolean;
  showBaseBasketSingle: boolean;
  showSystemAccount: boolean;
  showLocalAccount: boolean;
  showPercentage: boolean;
  showScheduleM: boolean;
  showLocalAccountDrop: boolean;

  // Field disabled states (for edit mode)
  levelDisabled: boolean;
  levelEntityDisabled: boolean;
  grossIncomeDisabled: boolean;
  mapDisabled: boolean;
  methodDisabled: boolean;
  baseFieldsDisabled: boolean;

  // Form values
  level: LevelOption | null;
  filingGroup: ParentMeOption | null;
  parentME: ParentMeOption | null;
  meInfo: ParentMeOption | null;
  grossIncome: GrossIncomeOption | null;
  transactionCategory: string;
  mapTo: MapOption | null;
  methodKey: MethodOption | null;
  percentage: number;

  // Multiselect values
  systemAccount: SystemAccountOption[];
  localAccounts: LocalAccountOption[];
  scheduleMDetails: ScheduleMOption[];
  selectedBaseFdiiCodes: BaseCodeOption[] | string;
  selectedBaseBasketCode: BaseCodeOption[] | string;
  selectedBaseTypeCode: BaseCodeOption[];
  selectedBaseLevelCode: number | null;
  selectedBaseEntityLevelCode: number | null;

  // Helper flags
  isINC: boolean;
  checkInGroup: boolean;
  isChanged: boolean;
  isDelete: string;
  newArray: string[];
}

// ============================================
// COMPONENT
// ============================================
@Component({
    selector: 'gtw-expense-trans-add-edit',
    templateUrl: './expense-trans-add-edit.component.html',
    styleUrls: ['./expense-trans-add-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ExpenseTransAddEditService],
    standalone: false
})
export class ExpenseTransAddEditComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  private readonly subs = new Subscription();

  // ============================================
  // Workspace context (was @Input bindings)
  // ============================================
  private baseURLs: any = {};
  private globalService: any = {};
  private filterParams: any = {};
  private rowData: TransCategoryRowData = {};
  private gridData: any = {};
  private GENERAL_CONFIG: any = {};

  private isInitialized = false;

  // ============================================
  // OUTPUTS
  // ============================================
  @Output('request-grid-update')
  requestGridUpdate = new EventEmitter<string>();

  // ============================================
  // COMPONENT PROPERTIES
  // ============================================
  minimise = false;
  modalTitle = 'Add Trans Category';

  // Form state - sterowanie formularzem przez obiekt
  formState: FormState = this.getInitialFormState();

  // Dropdown options
  levelDetails: LevelOption[] = [];
  filingDetails: ParentMeOption[] = [];
  parentDetails: ParentMeOption[] = [];
  meDetails: ParentMeOption[] = [];
  grossIncomeDetails: GrossIncomeOption[] = [];
  mapDetails: MapOption[] = [];
  mapCopyDetails: MapOption[] = [];
  methodKeys: MethodOption[] = [];
  systemAccountDetails: SystemAccountOption[] = [];
  accountScheduleData: LocalAccountOption[] = [];
  scheduleMOptions: ScheduleMOption[] = [];

  // Custom multiselect dropdown state
  systemAccountDropdownOpen = false;
  localAccountDropdownOpen = false;
  scheduleMDropdownOpen = false;
  baseFdiiDropdownOpen = false;
  baseBasketDropdownOpen = false;
  baseTypeDropdownOpen = false;
  systemAccountSearchText = '';
  localAccountSearchText = '';
  scheduleMSearchText = '';
  baseFdiiSearchText = '';
  baseBasketSearchText = '';
  baseTypeSearchText = '';

  // Loading states for multiselects
  isLoadingSystemAccounts = false;
  isLoadingLocalAccounts = false;

  // Delete confirmation modal
  showDeleteModal = false;
  deleteLoading = false;
  modalConfig: ConfirmationModalConfig = {
    title: 'Remove Trans Category',
    message: 'Are you sure to remove Trans Category?',
    iconClass: 'fa fa-trash',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  // Base options
  baseFDIIData: BaseCodeOption[] = [];
  baseBasketData: BaseCodeOption[] = [];
  baseTypeData: BaseCodeOption[] = [];
  baseLevelData: BaseCodeOption[] = [];
  baseEntityData: BaseCodeOption[] = [];
  basketCodeAllocateData: BaseCodeOption[] = [];
  basefdiiSingleMultiData: BaseCodeOption[] = [];
  baseBasketTypeData: BaseCodeOption[] = [];
  baseTypeCodeData: BaseCodeOption[] = [];

  // Helper data
  attribMastData: AttribMastOption[] = [];
  allTransCategoryData: any[] = [];
  allGPTransCategoryData: any[] = [];
  accountInformation: any[] = [];
  scheduleMInformation: any[] = [];

  // Check expense collection
  checkInGrossExpenseCollection = ['Interest Expense', 'Research', 'Expenses A&A', 'Asset Exp'];

  // Method tracking - to detect Allocate -> non-Allocate transitions (matching AngularJS behavior)
  private previousMethodDescription: string | null = null;
  private isBaseFieldsInitialized = false;

  // ============================================
  // CONSTRUCTOR
  // ============================================
  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private service: ExpenseTransAddEditService,
    private cdr: ChangeDetectorRef,
    private _snackBar: MatSnackBar,
    private elementRef: ElementRef
  ) {}

  // ============================================
  // HOST LISTENER - Click outside to close dropdowns
  // ============================================
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    
    // Use data-dropdown attribute to identify specific dropdowns (not index-based)
    const systemAccountDropdown = this.elementRef.nativeElement.querySelector('[data-dropdown="systemAccount"]');
    const localAccountDropdown = this.elementRef.nativeElement.querySelector('[data-dropdown="localAccount"]');
    const scheduleMDropdown = this.elementRef.nativeElement.querySelector('[data-dropdown="scheduleM"]');
    const baseFdiiDropdown = this.elementRef.nativeElement.querySelector('[data-dropdown="baseFdii"]');
    const baseBasketDropdown = this.elementRef.nativeElement.querySelector('[data-dropdown="baseBasket"]');
    const baseTypeDropdown = this.elementRef.nativeElement.querySelector('[data-dropdown="baseType"]');
    
    // Check if click is inside each dropdown
    const isInsideSystemAccount = systemAccountDropdown?.contains(target);
    const isInsideLocalAccount = localAccountDropdown?.contains(target);
    const isInsideScheduleM = scheduleMDropdown?.contains(target);
    const isInsideBaseFdii = baseFdiiDropdown?.contains(target);
    const isInsideBaseBasket = baseBasketDropdown?.contains(target);
    const isInsideBaseType = baseTypeDropdown?.contains(target);
    
    // Close System Account dropdown if clicking outside it
    if (this.systemAccountDropdownOpen && !isInsideSystemAccount) {
      this.systemAccountDropdownOpen = false;
      this.systemAccountSearchText = '';
      this.cdr.detectChanges();
    }
    
    // Close Local Account dropdown if clicking outside it
    if (this.localAccountDropdownOpen && !isInsideLocalAccount) {
      this.localAccountDropdownOpen = false;
      this.localAccountSearchText = '';
      this.cdr.detectChanges();
    }
    
    // Close Schedule M dropdown if clicking outside it
    if (this.scheduleMDropdownOpen && !isInsideScheduleM) {
      this.scheduleMDropdownOpen = false;
      this.scheduleMSearchText = '';
      this.cdr.detectChanges();
    }
    
    // Close Base FDII dropdown if clicking outside it
    if (this.baseFdiiDropdownOpen && !isInsideBaseFdii) {
      this.baseFdiiDropdownOpen = false;
      this.baseFdiiSearchText = '';
      this.cdr.detectChanges();
    }
    
    // Close Base Basket dropdown if clicking outside it
    if (this.baseBasketDropdownOpen && !isInsideBaseBasket) {
      this.baseBasketDropdownOpen = false;
      this.baseBasketSearchText = '';
      this.cdr.detectChanges();
    }
    
    // Close Base Type dropdown if clicking outside it
    if (this.baseTypeDropdownOpen && !isInsideBaseType) {
      this.baseTypeDropdownOpen = false;
      this.baseTypeSearchText = '';
      this.cdr.detectChanges();
    }
  }

  // ============================================
  // LIFECYCLE
  // ============================================
  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const gp = this.gtwGlobal.hydrateGlobalParamsFromContext(activeScreen, this.filterParams);
    this.globalService = { ...header, globalParams: gp };
    const row = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = (Array.isArray(row) ? (row[0] || {}) : row) as TransCategoryRowData;
    this.gridData = gridPayloadFromActiveScreen(activeScreen) || {};
  }

  private tryInit(): void {
    if (this.isInitialized) {
      return;
    }

    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.base_url;
    if (!baseUrl) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    if (this.gridData?.rowData && Object.keys(this.gridData.rowData).length > 0) {
      this.rowData = this.gridData.rowData;
    }

    const hasRowData = this.rowData && Object.keys(this.rowData).length > 0 && this.rowData.TRANS_CATG_KEY;
    const isCreate = !hasRowData;

    this.formState.isCreate = isCreate;
    this.formState.isEditMode = !isCreate;

    this.modalTitle = isCreate ? 'Add Trans Category' : 'Edit Trans Category';

    // Use rowData from either source (prefer direct input, fallback to gridData.rowData)
    if (!this.rowData?.TRANS_CATG_KEY && this.gridData?.rowData?.TRANS_CATG_KEY) {
      this.rowData = this.gridData.rowData;
    }

    // Set initial values from rowData if editing
    if (!isCreate && this.rowData) {
      this.formState.percentage = this.rowData.PERCENTAGE || 100;
      this.formState.transactionCategory = this.rowData.TRANS_CATG_NAME || '';
      this.formState.isDelete = this.rowData.IS_DELETE || 'N';
      
      // In Edit mode, disable certain fields (matches original behavior)
      this.formState.levelDisabled = true;
      this.formState.levelEntityDisabled = true;
      this.formState.grossIncomeDisabled = true;
      this.formState.mapDisabled = true;
      this.formState.methodDisabled = true;
      this.formState.baseFieldsDisabled = true;
    } else {
      this.formState.percentage = 100;
    }

    // Load initial data
    this.loadInitialData();
  }

  private getInitialFormState(): FormState {
    return {
      isEditMode: false,
      isCreate: true,
      isLoading: true,
      isLoadingMapDetails: false,
      isSaveClicked: false,
      crudLoading: false,

      level1Show: false,
      level2Show: false,
      showMethodDesc: false,
      showBaseFdii: false,
      showBaseBasket: false,
      showBaseType: false,
      showBaseLevel: false,
      showBaseEntityLevel: false,
      showBaseFdiiSingle: false,
      showBaseBasketSingle: false,
      showSystemAccount: false,
      showLocalAccount: false,
      showPercentage: false,
      showScheduleM: false,
      showLocalAccountDrop: false,

      levelDisabled: false,
      levelEntityDisabled: false,
      grossIncomeDisabled: false,
      mapDisabled: false,
      methodDisabled: false,
      baseFieldsDisabled: false,

      level: null,
      filingGroup: null,
      parentME: null,
      meInfo: null,
      grossIncome: null,
      transactionCategory: '',
      mapTo: null,
      methodKey: null,
      percentage: 100,

      systemAccount: [],
      localAccounts: [],
      scheduleMDetails: [],
      selectedBaseFdiiCodes: [],
      selectedBaseBasketCode: [],
      selectedBaseTypeCode: [],
      selectedBaseLevelCode: null,
      selectedBaseEntityLevelCode: null,

      isINC: false,
      checkInGroup: false,
      isChanged: false,
      isDelete: 'N',
      newArray: []
    };
  }

  // ============================================
  // DATA LOADING
  // ============================================
  private loadInitialData(): void {
    this.formState.isLoading = true;
    this.cdr.detectChanges();

    const baseUrl = this.GENERAL_CONFIG?.base_url || this.baseURLs?.base_url;
    const scenario = this.globalService?.globalParams?.scenario ?? this.filterParams?.scenario;
    const jcdKey = this.globalService?.globalParams?.jcd_key ?? this.filterParams?.jcd_key;

    this.service.setGlobalParams(this.globalService?.globalParams || {});
    this.service.setFilterParams(this.filterParams || {});

    // Load all initial data in parallel using forkJoin
    forkJoin({
      levelMapDetails: this.service.getLevelAndMapDetails(baseUrl).pipe(catchError(e => { console.error('Error loading level/map:', e); return of([]); })),
      parentMeDetails: this.service.getParentMeDetails(baseUrl).pipe(catchError(e => { console.error('Error loading parent ME:', e); return of([]); })),
      grossIncomeDetails: this.service.getGrossIncomeDetails(baseUrl).pipe(catchError(e => { console.error('Error loading gross income:', e); return of([]); })),
      attribMastData: this.service.getAttribMast(baseUrl).pipe(catchError(e => { console.error('Error loading attrib mast:', e); return of([]); })),
      codeMastData: this.service.getCodeMastData(baseUrl, scenario, jcdKey).pipe(catchError(e => { console.error('Error loading code mast:', e); return of([]); }))
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(results => {
      // Process level and map details
      this.levelDetails = results.levelMapDetails.filter((d: any) => d.CODE_GROUP === 'FTC_LEVEL');
      this.mapDetails = results.levelMapDetails.filter((d: any) => d.CODE_GROUP === 'FTC_TRANS_CATG_MAP');
      this.mapCopyDetails = [...this.mapDetails];
      this.methodKeys = results.levelMapDetails.filter((d: any) => d.CODE_GROUP === 'METHOD_CODE');

      // Process parent ME details
      this.parentDetails = results.parentMeDetails.filter((d: ParentMeOption) => d.LEVEL_GROUP === 'Parent ME');
      this.filingDetails = results.parentMeDetails.filter((d: ParentMeOption) => d.LEVEL_GROUP === 'Filing Group');
      this.meDetails = results.parentMeDetails.filter((d: ParentMeOption) => d.LEVEL_GROUP === 'ME');

      // Process gross income details
      this.grossIncomeDetails = results.grossIncomeDetails;

      // Process attrib mast
      this.attribMastData = results.attribMastData;

      // Process code mast data
      this.basketCodeAllocateData = results.codeMastData.filter((d: any) => d.CODE_GROUP === 'SOURCE_CODE_ALLOCATE')
        .filter((d: any) => d.CODE_DESCRIPTION !== 'Default');
      this.baseFDIIData = results.codeMastData.filter((d: any) => d.CODE_GROUP === 'FDII_CODE');
      this.baseBasketData = results.codeMastData.filter((d: any) => d.CODE_GROUP === 'SOURCE_CODE')
        .filter((d: any) => d.CODE_DESCRIPTION !== 'Default');
      this.baseLevelData = results.codeMastData.filter((d: any) => d.CODE_GROUP === 'BASE_LEVEL_CODE');
      this.baseEntityData = results.codeMastData.filter((d: any) => d.CODE_GROUP === 'BASE_ENTITY_CODE');
      this.baseTypeCodeData = results.codeMastData.filter((d: any) => d.CODE_GROUP === 'BASE_TYPE_CODE');
      this.baseBasketTypeData = results.codeMastData.filter((d: any) => d.CODE_GROUP === 'BASE_BASKET_CODE');
      this.basefdiiSingleMultiData = results.codeMastData.filter((d: any) => d.CODE_GROUP === 'BASE_FDII_CODE');

      // Set values for edit mode
      if (!this.formState.isCreate && this.rowData) {
        // Level, Map - with type conversion for safety
        this.formState.level = this.levelDetails.find(l => 
          l.CODE_KEY === this.rowData.LEVEL_KEY || String(l.CODE_KEY) === String(this.rowData.LEVEL_KEY)
        ) || null;
        this.formState.mapTo = this.mapDetails.find(m => 
          m.CODE_KEY === this.rowData.MAP_KEY || String(m.CODE_KEY) === String(this.rowData.MAP_KEY)
        ) || null;
        
        // Method - find by CODE_KEY OR by CODE_DESCRIPTION (METHOD_CODE field)
        this.formState.methodKey = this.methodKeys.find(m => 
          m.CODE_KEY === this.rowData.METHOD_KEY || 
          String(m.CODE_KEY) === String(this.rowData.METHOD_KEY) ||
          m.CODE_DESCRIPTION === this.rowData.METHOD_CODE
        ) || null;
        
        console.log('[Edit Mode] Method loading:', {
          methodKey: this.rowData.METHOD_KEY,
          methodCode: this.rowData.METHOD_CODE,
          foundMethod: this.formState.methodKey,
          availableMethods: this.methodKeys.map(m => ({ key: m.CODE_KEY, desc: m.CODE_DESCRIPTION }))
        });
        
        // Set level1Show for edit mode
        if (this.formState.level) {
          this.formState.level1Show = true;
        }

        // ME values - ME_KEY can be string or number, convert to number for comparison
        if (this.rowData?.ME_KEY) {
          const meKey = typeof this.rowData.ME_KEY === 'string' ? parseInt(this.rowData.ME_KEY, 10) : this.rowData.ME_KEY;
          this.formState.parentME = this.parentDetails.find(p => p.PARENT_ME_KEY === meKey) || null;
          this.formState.filingGroup = this.filingDetails.find(p => p.PARENT_ME_KEY === meKey) || null;
          this.formState.meInfo = this.meDetails.find(p => p.PARENT_ME_KEY === meKey) || null;
        }

        // Gross income - compare with type conversion (GIE_KEY and ACCT_KEY may be string or number)
        const gieKey = this.rowData.GIE_KEY;
        this.formState.grossIncome = this.grossIncomeDetails.find(g => {
          // Compare as strings to handle type mismatches
          return String(g.ACCT_KEY) === String(gieKey) || g.ACCT_KEY === gieKey;
        }) || null;
      } else {
        // Create mode defaults
        this.formState.selectedBaseFdiiCodes = [...this.baseFDIIData];
        this.formState.selectedBaseBasketCode = [...this.baseBasketData];
        if (this.baseLevelData.length > 0) {
          this.formState.selectedBaseLevelCode = this.baseLevelData[0].CODE_KEY;
        }
        if (this.baseEntityData.length > 0) {
          this.formState.selectedBaseEntityLevelCode = this.baseEntityData[0].CODE_KEY;
        }
      }

      // For Edit mode, load additional data AFTER initial data is processed
      if (!this.formState.isCreate) {
        this.loadEditModeData();
      } else {
        this.formState.isLoading = false;
      }

      this.cdr.detectChanges();
    },
    (error: any) => {
      console.error('[expense-trans-add-edit] loadInitialData forkJoin error:', error);
      this.formState.isLoading = false;
      this.cdr.detectChanges();
    });
  }

  private loadGrossIncomeDetails(): void {
    const baseUrl = this.GENERAL_CONFIG.base_url;

    this.service.getGrossIncomeDetails(baseUrl).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: GrossIncomeOption[]) => {
        this.grossIncomeDetails = data;
        this.cdr.detectChanges();
      },
      error => {
        console.error('Error loading gross income details:', error);
      }
    );
  }

  /**
   * Load additional data for edit mode (account info, schedule M info, base edit data)
   */
  private loadEditModeData(): void {
    if (this.formState.isCreate || !this.rowData?.TRANS_CATG_KEY) {
      return;
    }

    const baseUrl = this.GENERAL_CONFIG.base_url;
    const transCatgKey = this.rowData.TRANS_CATG_KEY;
    const ssoId = this.globalService?.userSettingsData?.user?.sso_id || '';
    const taxYear = parseInt(this.globalService?.globalParams?.tax_year || this.filterParams?.tax_year, 10);
    const scenario = parseInt(this.globalService?.globalParams?.scenario || this.filterParams?.scenario, 10);

    // IMPORTANT: Load methods specific to this gross income (matching AngularJS getMethod)
    // This must be done before visibility update so methodKey can be found
    if (this.formState.grossIncome?.ACCT_KEY) {
      this.loadMethodOptionsForEdit();
    }

    // Update visibility based on loaded mapTo
    this.updateFormVisibility();

    // Load account info if map type is "by Account"
    if (this.rowData.MAP_DESC === 'by Account' || this.rowData.MAPTO === 'by Account') {
      // Set visibility flags for Map Details
      this.formState.level2Show = true;
      this.formState.showSystemAccount = true;
      this.formState.showLocalAccount = this.formState.grossIncome?.DISP_ACCT_CODE !== 'Asset Exp';
      this.formState.showPercentage = true;
      this.formState.showLocalAccountDrop = true;
      
      // Show loaders for Map Details section
      this.formState.isLoadingMapDetails = true;
      this.isLoadingSystemAccounts = true;
      this.isLoadingLocalAccounts = true;
      this.cdr.detectChanges();

      // OPTIMIZED: Load system accounts and account info in PARALLEL
      const grFlag = this.formState.grossIncome?.DISP_ACCT_CODE === 'Asset Exp' ? 'A' : 'E';
      const acctKey = this.formState.grossIncome?.ACCT_KEY || 0;
      
      forkJoin({
        systemAccounts: this.service.getSystemAccounts(baseUrl, acctKey, grFlag).pipe(
          catchError(e => { console.error('Error loading system accounts:', e); return of([]); })
        ),
        accountInfo: this.service.getAccountInfo(baseUrl, transCatgKey).pipe(
          catchError(e => { console.error('Error loading account info:', e); return of([]); })
        )
      }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(results => {
        // Process system accounts
        this.systemAccountDetails = results.systemAccounts.length > 0 
          ? results.systemAccounts 
          : [{ ACCT_KEY: 'All', DESCRIPTION: 'ALL' }];
        
        if (!this.systemAccountDetails.find(s => s.ACCT_KEY === 'All')) {
          this.systemAccountDetails.unshift({ ACCT_KEY: 'All', DESCRIPTION: 'ALL' });
        }
        
        // Hide System Account loader - data is ready
        this.isLoadingSystemAccounts = false;
        
        // Process account info and map selected system accounts
        this.accountInformation = results.accountInfo;
        if (results.accountInfo && results.accountInfo.length > 0) {
          const sysAcctKeys = results.accountInfo.map((d: any) => d.P_SYS_ACCT_KEY).filter((k: any) => k);
          
          if (sysAcctKeys.length > 0) {
            this.formState.systemAccount = this.systemAccountDetails.filter(s => {
              const sAcctKey = typeof s.ACCT_KEY === 'string' ? s.ACCT_KEY : String(s.ACCT_KEY);
              return sysAcctKeys.some((k: any) => String(k) === sAcctKey || k === s.ACCT_KEY);
            });
          }
          
          // Load local accounts (this still needs systemAccount to be set first)
          this.loadLocalAccountsForEdit(sysAcctKeys, results.accountInfo);
        } else {
          // No account info - hide all loaders
          this.isLoadingLocalAccounts = false;
          this.formState.isLoadingMapDetails = false;
        }
        
        this.cdr.detectChanges();
      });
    }

    // Load schedule M info if map type is "by Schedule M"
    if (this.rowData.MAP_DESC === 'by Schedule M' || this.rowData.MAPTO === 'by Schedule M') {
      // Set visibility flags for Map Details
      this.formState.level2Show = true;
      this.formState.showScheduleM = true;
      
      // Show loader for Map Details section
      this.formState.isLoadingMapDetails = true;

      // First load schedule M options, then load schedule M info
      this.service.getScheduleMOptions(baseUrl).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (schMOptions: ScheduleMOption[]) => {
          this.scheduleMOptions = schMOptions;
          
          // Now load schedule M info
          this.service.getScheduleMInfo(baseUrl, transCatgKey).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
            (data: any[]) => {
              this.scheduleMInformation = data;
              if (data && data.length > 0) {
                // Set schedule M details from loaded data
                const templateIds = data.map(d => d.M_TEMPLATE_KEY).filter(id => id);
                if (templateIds.length > 0) {
                  this.formState.scheduleMDetails = this.scheduleMOptions.filter(
                    s => templateIds.some(id => String(id) === String(s.SCHDM_TEMPLATE_ID))
                  );
                }
              }
              // Hide loader
              this.formState.isLoadingMapDetails = false;
              this.cdr.detectChanges();
            },
            error => {
              console.error('Error loading schedule M info:', error);
              this.formState.isLoadingMapDetails = false;
              this.cdr.detectChanges();
            }
          );
        },
        error => {
          console.error('Error loading schedule M options for edit:', error);
          this.formState.isLoadingMapDetails = false;
          this.cdr.detectChanges();
        }
      );
    }

    // Load base edit data for Expenses A&A and Asset Exp
    const grossIncomeCode = this.formState.grossIncome?.DISP_ACCT_CODE;
    const isExpensesOrAsset = grossIncomeCode === 'Expenses A&A' || grossIncomeCode === 'Asset Exp' ||
                              grossIncomeCode === 'Interest Expense' || grossIncomeCode === 'Research';
    
    if (isExpensesOrAsset) {
      console.log('[Edit Mode] Loading base data for:', grossIncomeCode);
      
      // IMPORTANT: Load base type data first (needed for visibility)
      this.loadAllTransCategoryForEdit();
      
      // Load Base FDII Edit
      if (this.rowData.BASE_FDII && this.rowData.BASE_FDII !== 'ALL') {
        this.service.getBaseFdiiEdit(baseUrl, ssoId, taxYear, scenario, transCatgKey).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
          (data: any[]) => {
            if (data && data.length > 0) {
              // API returns CODE_DESCRIPTION, but baseFDIIData may have CODE_DESC
              // Match using both properties (matching AngularJS: _.find(baseFDIIData, ['CODE_DESC', d.CODE_DESCRIPTION]))
              const fdiiDescriptions = data.map(d => d.CODE_DESCRIPTION);
              this.formState.selectedBaseFdiiCodes = this.baseFDIIData.filter(b => 
                fdiiDescriptions.includes(b.CODE_DESC) || fdiiDescriptions.includes(b.CODE_DESCRIPTION)
              );
              console.log('[Edit Mode] Base FDII loaded:', {
                editData: data,
                matched: this.formState.selectedBaseFdiiCodes.length,
                baseFDIIDataSample: this.baseFDIIData[0]
              });
            }
            this.cdr.detectChanges();
          },
          error => console.error('Error loading base FDII edit:', error)
        );
      } else if (this.rowData.BASE_FDII === 'ALL') {
        this.formState.selectedBaseFdiiCodes = [...this.baseFDIIData];
        console.log('[Edit Mode] Base FDII set to ALL:', this.baseFDIIData.length, 'items');
      }

      // Load Base Basket Edit
      if (this.rowData.BASE_BASKET && this.rowData.BASE_BASKET !== 'ALL') {
        this.service.getBaseBasketEdit(baseUrl, ssoId, taxYear, scenario, transCatgKey).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
          (data: any[]) => {
            if (data && data.length > 0) {
              // API returns CODE_DESCRIPTION, but baseBasketData may have CODE_DESC
              // Match using both properties (matching AngularJS: _.find(baseBasketData, ['CODE_DESC', d.CODE_DESCRIPTION]))
              const basketDescriptions = data.map(d => d.CODE_DESCRIPTION);
              this.formState.selectedBaseBasketCode = this.baseBasketData.filter(b => 
                basketDescriptions.includes(b.CODE_DESC) || basketDescriptions.includes(b.CODE_DESCRIPTION)
              );
              console.log('[Edit Mode] Base Basket loaded:', {
                editData: data,
                matched: this.formState.selectedBaseBasketCode.length,
                baseBasketDataSample: this.baseBasketData[0]
              });
            }
            this.cdr.detectChanges();
          },
          error => console.error('Error loading base basket edit:', error)
        );
      } else if (this.rowData.BASE_BASKET === 'ALL') {
        this.formState.selectedBaseBasketCode = [...this.baseBasketData];
        console.log('[Edit Mode] Base Basket set to ALL:', this.baseBasketData.length, 'items');
      }

      // Load Base Type Edit
      if (this.rowData.BASE_TYPE && this.rowData.BASE_TYPE !== 'ALL') {
        this.service.getBaseTypeEdit(baseUrl, ssoId, taxYear, scenario, transCatgKey).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
          (data: any[]) => {
            if (data && data.length > 0) {
              this.formState.selectedBaseTypeCode = data;
              // Don't overwrite baseTypeData here - it's set by loadAllTransCategoryForEdit
              console.log('[Edit Mode] Base Type loaded:', data.length, 'items');
            }
            this.updateFormVisibility();
            this.cdr.detectChanges();
          },
          error => console.error('Error loading base type edit:', error)
        );
      } else if (this.rowData.BASE_TYPE === 'ALL') {
        // Will be set after baseTypeData is loaded in loadAllTransCategoryForEdit
        console.log('[Edit Mode] Base Type is ALL - will be set after data loads');
      }

      // Set base level and entity level from rowData
      if (this.rowData.BASE_LEVEL) {
        const baseLevel = this.baseLevelData.find(b => b.CODE_DESCRIPTION === this.rowData.BASE_LEVEL);
        if (baseLevel) {
          this.formState.selectedBaseLevelCode = baseLevel.CODE_KEY;
        }
      }

      if (this.rowData.BASE_ENTITY_LEVEL) {
        const baseEntity = this.baseEntityData.find(b => b.CODE_DESCRIPTION === this.rowData.BASE_ENTITY_LEVEL);
        if (baseEntity) {
          this.formState.selectedBaseEntityLevelCode = baseEntity.CODE_KEY;
        }
      }
    }

    // Mark as changed false initially in edit mode
    this.formState.isChanged = false;
    
    // Finished loading edit mode data - hide loader
    // Use setTimeout to ensure all async operations have triggered detectChanges
    setTimeout(() => {
      this.formState.isLoading = false;
      this.cdr.detectChanges();
    }, 100);
  }

  private loadCodeMastData(): void {
    const baseUrl = this.GENERAL_CONFIG.base_url;
    const scenario = this.globalService?.globalParams?.scenario ?? this.filterParams?.scenario;
    const jcdKey = this.globalService?.globalParams?.jcd_key ?? this.filterParams?.jcd_key;

    this.service.getCodeMastData(baseUrl, scenario, jcdKey).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any[]) => {
        this.basketCodeAllocateData = data.filter(d => d.CODE_GROUP === 'SOURCE_CODE_ALLOCATE')
          .filter(d => d.CODE_DESCRIPTION !== 'Default');
        this.baseFDIIData = data.filter(d => d.CODE_GROUP === 'FDII_CODE');
        this.baseBasketData = data.filter(d => d.CODE_GROUP === 'SOURCE_CODE')
          .filter(d => d.CODE_DESCRIPTION !== 'Default');
        this.baseLevelData = data.filter(d => d.CODE_GROUP === 'BASE_LEVEL_CODE');
        this.baseEntityData = data.filter(d => d.CODE_GROUP === 'BASE_ENTITY_CODE');
        this.baseTypeCodeData = data.filter(d => d.CODE_GROUP === 'BASE_TYPE_CODE');
        this.baseBasketTypeData = data.filter(d => d.CODE_GROUP === 'BASE_BASKET_CODE');
        this.basefdiiSingleMultiData = data.filter(d => d.CODE_GROUP === 'BASE_FDII_CODE');

        // Set defaults for create mode
        if (this.formState.isCreate) {
          this.formState.selectedBaseFdiiCodes = [...this.baseFDIIData];
          this.formState.selectedBaseBasketCode = [...this.baseBasketData];
          if (this.baseLevelData.length > 0) {
            this.formState.selectedBaseLevelCode = this.baseLevelData[0].CODE_KEY;
          }
          if (this.baseEntityData.length > 0) {
            this.formState.selectedBaseEntityLevelCode = this.baseEntityData[0].CODE_KEY;
          }
          // Mark base fields as initialized to prevent reset on subsequent method changes
          this.isBaseFieldsInitialized = true;
        }

        this.cdr.detectChanges();
      },
      error => {
        console.error('Error loading code mast data:', error);
      }
    );
  }

  private loadMethodOptions(): void {
    if (!this.formState.grossIncome?.ACCT_KEY) return;

    const baseUrl = this.GENERAL_CONFIG.base_url;
    const scenario = this.globalService?.globalParams?.scenario ?? this.filterParams?.scenario;

    this.service.getMethodOptions(baseUrl, this.formState.grossIncome.ACCT_KEY, scenario).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: MethodOption[]) => {
        this.methodKeys = data;

        // Set default for Expenses A&A
        if (this.formState.grossIncome?.DISP_ACCT_CODE === 'Expenses A&A' && this.formState.isCreate) {
          const defaultMethod = this.methodKeys.find(m => m.CODE_DESCRIPTION === 'Best Method - FDII');
          if (defaultMethod) {
            this.formState.methodKey = defaultMethod;
          }
        }

        // Set value for edit mode
        if (!this.formState.isCreate && this.rowData?.METHOD_CODE) {
          this.formState.methodKey = this.methodKeys.find(m => m.CODE_DESCRIPTION === this.rowData.METHOD_CODE) || null;
        }

        this.cdr.detectChanges();
      },
      error => {
        console.error('Error loading method options:', error);
      }
    );
  }

  /**
   * Load method options specifically for EDIT mode
   * This is needed because methods are specific to the selected gross income
   */
  private loadMethodOptionsForEdit(): void {
    if (!this.formState.grossIncome?.ACCT_KEY) return;

    const baseUrl = this.GENERAL_CONFIG.base_url;
    const scenario = this.globalService?.globalParams?.scenario ?? this.filterParams?.scenario;

    this.service.getMethodOptions(baseUrl, this.formState.grossIncome.ACCT_KEY, scenario).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: MethodOption[]) => {
        this.methodKeys = data;
        
        // Set method from rowData
        if (this.rowData?.METHOD_CODE) {
          this.formState.methodKey = this.methodKeys.find(m => 
            m.CODE_DESCRIPTION === this.rowData.METHOD_CODE ||
            m.CODE_KEY === this.rowData.METHOD_KEY ||
            String(m.CODE_KEY) === String(this.rowData.METHOD_KEY)
          ) || null;
          
          console.log('[loadMethodOptionsForEdit] Method set:', {
            methodCode: this.rowData.METHOD_CODE,
            methodKey: this.rowData.METHOD_KEY,
            foundMethod: this.formState.methodKey,
            availableMethods: this.methodKeys.length
          });
        }

        // Update visibility after method is loaded (important for base fields visibility)
        this.updateFormVisibility();
        this.cdr.detectChanges();
      },
      error => {
        console.error('Error loading method options for edit:', error);
      }
    );
  }

  private loadSystemAccounts(): void {
    if (!this.formState.grossIncome?.ACCT_KEY) return;

    const baseUrl = this.GENERAL_CONFIG.base_url;
    const grFlag = this.formState.grossIncome.DISP_ACCT_CODE === 'Asset Exp' ? 'A' : 'E';

    // Show loader
    this.isLoadingSystemAccounts = true;
    this.cdr.detectChanges();

    this.service.getSystemAccounts(baseUrl, this.formState.grossIncome.ACCT_KEY, grFlag).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: SystemAccountOption[]) => {
        this.systemAccountDetails = data;
        if (this.systemAccountDetails.length === 0) {
          this.systemAccountDetails = [{ ACCT_KEY: 'All', DESCRIPTION: 'ALL' }];
        } else {
          this.systemAccountDetails.unshift({ ACCT_KEY: 'All', DESCRIPTION: 'ALL' });
        }
        // Hide loader
        this.isLoadingSystemAccounts = false;
        this.cdr.detectChanges();
      },
      error => {
        console.error('Error loading system accounts:', error);
        this.isLoadingSystemAccounts = false;
        this.cdr.detectChanges();
      }
    );
  }


  private loadScheduleMOptions(): void {
    const baseUrl = this.GENERAL_CONFIG.base_url;

    // Show loader while loading Schedule M options
    this.formState.isLoadingMapDetails = true;
    this.cdr.detectChanges();

    this.service.getScheduleMOptions(baseUrl).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: ScheduleMOption[]) => {
        this.scheduleMOptions = data;
        // Hide loader
        this.formState.isLoadingMapDetails = false;
        this.cdr.detectChanges();
      },
      error => {
        console.error('Error loading schedule M options:', error);
        // Hide loader on error
        this.formState.isLoadingMapDetails = false;
        this.cdr.detectChanges();
      }
    );
  }

  loadLocalAccounts(): void {
    if (this.formState.systemAccount.length === 0) {
      this.formState.showLocalAccountDrop = false;
      this.isLoadingLocalAccounts = false;
      return;
    }

    const acctKeys = this.formState.systemAccount.map(a => a.ACCT_KEY).join(',');
    // Get meKey from all possible sources (same as in edit mode)
    const meKey = this.formState.parentME?.PARENT_ME_KEY || 
                  this.formState.filingGroup?.PARENT_ME_KEY || 
                  this.formState.meInfo?.PARENT_ME_KEY || 
                  (this.rowData?.ME_KEY ? Number(this.rowData.ME_KEY) : null);
    const baseUrl = this.GENERAL_CONFIG.base_url;

    // Show loader while loading local accounts
    this.isLoadingLocalAccounts = true;
    this.cdr.detectChanges();

    this.service.getLocalAccounts(baseUrl, acctKeys, meKey).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: LocalAccountOption[]) => {
        this.accountScheduleData = data;
        if (this.accountScheduleData.length > 0) {
          this.accountScheduleData.unshift({ ACCT_KEY: 'All', ACCT_DESC: 'ALL' });
        }
        this.formState.showLocalAccountDrop = true;
        // Hide loader
        this.isLoadingLocalAccounts = false;
        this.cdr.detectChanges();
      },
      error => {
        console.error('Error loading local accounts:', error);
        this.formState.showLocalAccountDrop = false;
        // Hide loader on error too
        this.isLoadingLocalAccounts = false;
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Load local accounts for edit mode and set selected values
   */
  private loadLocalAccountsForEdit(sysAcctKeys: string[], accountInfoData: any[]): void {
    if (sysAcctKeys.length === 0) {
      this.isLoadingLocalAccounts = false;
      this.formState.isLoadingMapDetails = false;
      this.cdr.detectChanges();
      return;
    }

    const acctKeysStr = sysAcctKeys.join(',');
    const meKey = this.formState.parentME?.PARENT_ME_KEY || 
                  this.formState.filingGroup?.PARENT_ME_KEY || 
                  this.formState.meInfo?.PARENT_ME_KEY || 
                  (this.rowData?.ME_KEY ? Number(this.rowData.ME_KEY) : null);
    const baseUrl = this.GENERAL_CONFIG.base_url;

    // Show loader for local accounts
    this.isLoadingLocalAccounts = true;
    this.cdr.detectChanges();

    this.service.getLocalAccounts(baseUrl, acctKeysStr, meKey).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: LocalAccountOption[]) => {
        this.accountScheduleData = data;
        if (this.accountScheduleData.length > 0) {
          this.accountScheduleData.unshift({ ACCT_KEY: 'All', ACCT_DESC: 'ALL' });
        }

        // Set local accounts from accountInfoData - use P_LOCAL_ACCT_KEY (matching AngularJS)
        const localAcctKeys = accountInfoData.map(d => d.P_LOCAL_ACCT_KEY).filter(k => k);
        
        if (localAcctKeys.length > 0) {
          this.formState.localAccounts = this.accountScheduleData.filter(l => {
            const lAcctKey = typeof l.ACCT_KEY === 'string' ? l.ACCT_KEY : String(l.ACCT_KEY);
            return localAcctKeys.some(k => String(k) === lAcctKey || k === l.ACCT_KEY);
          });
        }

        this.formState.showLocalAccountDrop = true;
        // Hide loaders - all data loaded
        this.isLoadingLocalAccounts = false;
        this.formState.isLoadingMapDetails = false;
        this.cdr.detectChanges();
      },
      error => {
        console.error('Error loading local accounts for edit:', error);
        // Hide loaders even on error
        this.isLoadingLocalAccounts = false;
        this.formState.isLoadingMapDetails = false;
        this.cdr.detectChanges();
      }
    );
  }

  // ============================================
  // FORM STATE MANAGEMENT
  // ============================================
  private updateFormVisibility(): void {
    const state = this.formState;
    const grossIncome = state.grossIncome;
    const mapTo = state.mapTo;
    const methodKey = state.methodKey;

    // Reset newArray
    state.newArray = [];

    // Check if method should be shown
    if (grossIncome?.DISP_ACCT_CODE && this.checkInGrossExpenseCollection.includes(grossIncome.DISP_ACCT_CODE)) {
      state.newArray.push('method');
    }

    // Check for base fields visibility
    const isExpensesOrAsset = grossIncome?.DISP_ACCT_CODE === 'Expenses A&A' || grossIncome?.DISP_ACCT_CODE === 'Asset Exp';
    const isInterestExpense = grossIncome?.DISP_ACCT_CODE === 'Interest Expense';
    const isNotByAmount = mapTo?.CODE_DESCRIPTION !== 'by Amount';
    const isNotAllocate = methodKey?.CODE_DESCRIPTION !== 'Allocate';
    const isAllocate = methodKey?.CODE_DESCRIPTION === 'Allocate';

    if (isNotByAmount && isExpensesOrAsset && isNotAllocate) {
      state.newArray.push('selectedBaseFdiiCodes', 'selectedBaseBasketCode', 'selectedBaseTypeCode', 'selectedBaseLevelCode', 'selectedBaseEntityLevelCode');
    }

    if (isNotByAmount && (isInterestExpense || isExpensesOrAsset) && isAllocate) {
      state.newArray.push('selectedBaseFdiiCodes_single', 'selectedBaseBasketCode__single');
    }

    // Update visibility flags
    state.showMethodDesc = state.level1Show && state.newArray.includes('method') && mapTo?.CODE_DESCRIPTION !== 'by Amount';
    state.showBaseFdii = this.baseFDIIData.length > 0 && isNotByAmount && isExpensesOrAsset && isNotAllocate;
    state.showBaseBasket = this.baseBasketData.length > 0 && isNotByAmount && isExpensesOrAsset && isNotAllocate;
    state.showBaseType = this.baseTypeData.length > 0 && isNotByAmount && isExpensesOrAsset && isNotAllocate;
    state.showBaseLevel = this.baseLevelData.length > 0 && isNotByAmount && isExpensesOrAsset && isNotAllocate;
    state.showBaseEntityLevel = this.baseEntityData.length > 0 && isNotByAmount && isExpensesOrAsset && isNotAllocate;
    state.showBaseFdiiSingle = this.baseFDIIData.length > 0 && isNotByAmount && (isInterestExpense || isExpensesOrAsset) && isAllocate;
    state.showBaseBasketSingle = this.baseBasketData.length > 0 && isNotByAmount && (isInterestExpense || isExpensesOrAsset) && isAllocate;

    // SET DEFAULTS FOR CREATE MODE when Base fields become visible
    // This ensures defaults are set when controls appear for the first time
    if (state.isCreate) {
      // Base FDII - set defaults when visible and empty (multi-select mode)
      if (state.showBaseFdii && Array.isArray(state.selectedBaseFdiiCodes) && state.selectedBaseFdiiCodes.length === 0 && this.baseFDIIData.length > 0) {
        state.selectedBaseFdiiCodes = [...this.baseFDIIData];
        console.log('[updateFormVisibility] Set default Base FDII:', this.baseFDIIData.length, 'items');
      }
      
      // Base Basket - set defaults when visible and empty (multi-select mode)
      if (state.showBaseBasket && Array.isArray(state.selectedBaseBasketCode) && state.selectedBaseBasketCode.length === 0 && this.baseBasketData.length > 0) {
        state.selectedBaseBasketCode = [...this.baseBasketData];
        console.log('[updateFormVisibility] Set default Base Basket:', this.baseBasketData.length, 'items');
      }
      
      // Base Type - set defaults when visible and empty
      if (state.showBaseType && state.selectedBaseTypeCode.length === 0 && this.baseTypeData.length > 0) {
        state.selectedBaseTypeCode = [...this.baseTypeData];
        console.log('[updateFormVisibility] Set default Base Type:', this.baseTypeData.length, 'items');
      }
      
      // Base Level - set default to first item when visible and not set
      if (state.showBaseLevel && state.selectedBaseLevelCode === null && this.baseLevelData.length > 0) {
        state.selectedBaseLevelCode = this.baseLevelData[0].CODE_KEY;
        console.log('[updateFormVisibility] Set default Base Level:', this.baseLevelData[0].CODE_DESCRIPTION);
      }
      
      // Base Entity Level - set default to first item when visible and not set
      if (state.showBaseEntityLevel && state.selectedBaseEntityLevelCode === null && this.baseEntityData.length > 0) {
        state.selectedBaseEntityLevelCode = this.baseEntityData[0].CODE_KEY;
        console.log('[updateFormVisibility] Set default Base Entity Level:', this.baseEntityData[0].CODE_DESCRIPTION);
      }
    }

    // Map Details visibility
    state.level2Show = !!(grossIncome?.DISP_ACCT_CODE && mapTo?.CODE_DESCRIPTION && mapTo.CODE_DESCRIPTION !== 'by Amount');
    state.showSystemAccount = mapTo?.CODE_DESCRIPTION === 'by Account';
    state.showLocalAccount = mapTo?.CODE_DESCRIPTION === 'by Account' && grossIncome?.DISP_ACCT_CODE !== 'Asset Exp';
    state.showPercentage = mapTo?.CODE_DESCRIPTION === 'by Account';
    state.showScheduleM = mapTo?.CODE_DESCRIPTION === 'by Schedule M';

    // Edit mode - disable certain fields
    if (state.isEditMode) {
      state.levelDisabled = true;
      state.levelEntityDisabled = true;
      state.grossIncomeDisabled = true;
      state.mapDisabled = true;
      state.methodDisabled = true;
      state.baseFieldsDisabled = true;
    }

    this.cdr.detectChanges();
  }

  // ============================================
  // EVENT HANDLERS
  // ============================================
  onLevelChange(): void {
    const level = this.formState.level;

    // Reset dependent fields
    this.formState.filingGroup = null;
    this.formState.parentME = null;
    this.formState.meInfo = null;
    this.formState.grossIncome = null;
    this.formState.transactionCategory = '';
    this.formState.mapTo = null;
    this.formState.isINC = false;
    this.formState.level1Show = false;

    this.updateFormVisibility();
    this.loadGrossIncomeDetails();
  }

  onLevelEntityChange(): void {
    const level = this.formState.level;
    let entitySelected = false;

    switch (level?.CODE_KEY) {
      case 1: // Filing Group
        entitySelected = !!this.formState.filingGroup?.PARENT_ME_KEY;
        break;
      case 2: // Parent ME
        entitySelected = !!this.formState.parentME?.PARENT_ME_KEY;
        break;
      case 8689: // ME
        entitySelected = !!this.formState.meInfo?.PARENT_ME_KEY;
        break;
    }

    this.formState.level1Show = entitySelected;

    // Reset all dependent fields when entity changes (matching AngularJS openNextLevel_1)
    this.formState.grossIncome = null;
    this.formState.transactionCategory = '';
    this.formState.mapTo = null;
    this.formState.isINC = false;
    
    // Reset Map Details
    this.formState.systemAccount = [];
    this.formState.localAccounts = [];
    this.formState.scheduleMDetails = [];
    this.formState.percentage = 100;
    this.formState.showLocalAccountDrop = false;
    this.accountScheduleData = [];
    this.systemAccountDetails = [];
    
    // Reset Method and Base fields
    this.formState.methodKey = null;
    this.formState.selectedBaseFdiiCodes = [];
    this.formState.selectedBaseBasketCode = [];
    this.formState.selectedBaseTypeCode = [];
    this.formState.selectedBaseLevelCode = null;
    this.formState.selectedBaseEntityLevelCode = null;
    
    // Reset method tracking flags (base fields will be re-initialized)
    this.isBaseFieldsInitialized = false;
    this.previousMethodDescription = null;

    this.updateFormVisibility();
  }

  onGrossIncomeChange(): void {
    // Reset all dependent fields when grossIncome changes (matching AngularJS behavior)
    this.formState.transactionCategory = '';
    this.formState.mapTo = null;
    this.formState.isINC = false;
    
    // Reset Map Details
    this.formState.systemAccount = [];
    this.formState.localAccounts = [];
    this.formState.scheduleMDetails = [];
    this.formState.percentage = 100;
    this.formState.showLocalAccountDrop = false;
    this.accountScheduleData = [];
    this.systemAccountDetails = [];
    
    // Reset Method and Base fields
    this.formState.methodKey = null;
    this.formState.selectedBaseFdiiCodes = [];
    this.formState.selectedBaseBasketCode = [];
    this.formState.selectedBaseTypeCode = [];
    this.formState.selectedBaseLevelCode = null;
    this.formState.selectedBaseEntityLevelCode = null;
    
    // Reset method tracking flags (base fields will be re-initialized)
    this.isBaseFieldsInitialized = false;
    this.previousMethodDescription = null;

    // Update map options based on gross income
    if (this.formState.grossIncome?.DISP_ACCT_CODE === 'Asset Exp') {
      this.mapDetails = this.mapCopyDetails.filter(m => m.CODE_DESCRIPTION !== 'by Schedule M');
    } else {
      this.mapDetails = [...this.mapCopyDetails];
    }

    // Set default map for create mode
    if (this.formState.isCreate) {
      const byAmountMap = this.mapDetails.find(m => m.CODE_DESCRIPTION === 'by Amount');
      if (byAmountMap) {
        this.formState.mapTo = byAmountMap;
      }
    }

    this.updateFormVisibility();
    this.loadSystemAccounts();
    this.loadScheduleMOptions();
    this.loadMethodOptions();
    this.loadBaseType();

    this.cdr.detectChanges();
  }

  onMapChange(): void {
    const mapTo = this.formState.mapTo;

    // Reset map-dependent fields (matching AngularJS onChangeMapDetails)
    this.systemAccountDetails = [];
    this.formState.systemAccount = [];
    this.formState.localAccounts = [];
    this.formState.scheduleMDetails = [];
    this.formState.showLocalAccountDrop = false;
    this.accountScheduleData = [];
    this.formState.percentage = 100;

    if (mapTo?.CODE_DESCRIPTION === 'by Schedule M') {
      this.loadScheduleMOptions();
    } else if (mapTo?.CODE_DESCRIPTION === 'by Account') {
      this.loadSystemAccounts();
      
      // Set default methodKey ONLY for 'Expenses A&A' in create mode (matching AngularJS getMethod)
      // In AngularJS: if(vm.grossIncome && vm.grossIncome.DISP_ACCT_CODE == 'Expenses A&A') { vm.methodKey = exists; }
      if (this.formState.isCreate && this.methodKeys.length > 0 && 
          this.formState.grossIncome?.DISP_ACCT_CODE === 'Expenses A&A') {
        const bestMethodFdii = this.methodKeys.find(m => m.CODE_DESCRIPTION === 'Best Method - FDII');
        if (bestMethodFdii && !this.formState.methodKey) {
          this.formState.methodKey = bestMethodFdii;
        }
      }
    }

    // Load Base Type data when map is not "by Amount" and gross income is Asset Exp or Expenses A&A
    const grossIncomeCode = this.formState.grossIncome?.DISP_ACCT_CODE;
    console.log('[onMapChange] Checking if should load base type:', { 
      mapTo: mapTo?.CODE_DESCRIPTION, 
      grossIncomeCode, 
      isNotByAmount: mapTo?.CODE_DESCRIPTION !== 'by Amount',
      isExpensesOrAsset: grossIncomeCode === 'Asset Exp' || grossIncomeCode === 'Expenses A&A'
    });
    
    if (mapTo?.CODE_DESCRIPTION !== 'by Amount' && 
        (grossIncomeCode === 'Asset Exp' || grossIncomeCode === 'Expenses A&A')) {
      console.log('[onMapChange] Calling loadBaseType()');
      this.loadBaseType();
    }

    this.updateFormVisibility();
  }

  onMethodChange(): void {
    const methodKey = this.formState.methodKey;
    const currentMethodDesc = methodKey?.CODE_DESCRIPTION || null;
    
    // Determine if we should reset base fields (matching AngularJS changeMethodToAllocate logic)
    // Reset ONLY when:
    // 1. Switching TO 'Allocate' (sets single select defaults)
    // 2. Switching FROM 'Allocate' to non-Allocate (reset to ALL multi-select)
    // Do NOT reset when user is just selecting between non-Allocate methods
    // (defaults are already set in updateFormVisibility when controls become visible)
    
    const switchingToAllocate = currentMethodDesc === 'Allocate';
    const switchingFromAllocate = this.previousMethodDescription === 'Allocate' && currentMethodDesc !== 'Allocate';
    
    console.log('[onMethodChange] Method change:', {
      from: this.previousMethodDescription,
      to: currentMethodDesc,
      switchingToAllocate,
      switchingFromAllocate
    });

    if (switchingToAllocate) {
      // Set default single values for Allocate mode
      const defaultFdii = this.baseFDIIData.find(b => b.CODE_DESCRIPTION === 'US Derived DEI');
      const defaultBasket = this.basketCodeAllocateData.find(b => b.CODE_DESCRIPTION === 'U.S.');

      this.formState.selectedBaseFdiiCodes = defaultFdii ? String(defaultFdii.CODE_KEY) : '';
      this.formState.selectedBaseBasketCode = defaultBasket ? String(defaultBasket.CODE_KEY) : '';
    } else if (switchingFromAllocate) {
      // Reset to multi-select with all values when switching FROM Allocate
      this.formState.selectedBaseFdiiCodes = [...this.baseFDIIData];
      this.formState.selectedBaseBasketCode = [...this.baseBasketData];
    }
    // If neither condition, keep the user's selections as-is

    // Update baseTypeData based on method selection (matching AngularJS getAllBaseTypeByMethodChnage)
    // When method is 'Gross Income': use allTransCategoryData
    // Otherwise: use allGPTransCategoryData (filtered by GIE_NAME === 'GPROFIT')
    const isExpensesOrAsset = this.formState.grossIncome?.DISP_ACCT_CODE === 'Expenses A&A' || 
                              this.formState.grossIncome?.DISP_ACCT_CODE === 'Asset Exp' ||
                              this.formState.grossIncome?.DISP_ACCT_CODE === 'Interest Expense' ||
                              this.formState.grossIncome?.DISP_ACCT_CODE === 'Research';
    
    if (isExpensesOrAsset && this.allTransCategoryData.length > 0) {
      if (methodKey?.CODE_DESCRIPTION === 'Gross Income') {
        this.baseTypeData = [...this.allTransCategoryData];
      } else {
        this.baseTypeData = [...this.allGPTransCategoryData];
      }
      
      // Update selected base type values ONLY when switching to/from Allocate
      if (switchingToAllocate || switchingFromAllocate) {
        this.formState.selectedBaseTypeCode = [...this.baseTypeData];
      }
      console.log('[onMethodChange] baseTypeData updated:', this.baseTypeData.length, 'items, method:', methodKey?.CODE_DESCRIPTION);
    }

    // Update previous method for next change detection
    this.previousMethodDescription = currentMethodDesc;

    this.updateFormVisibility();
  }

  /**
   * Handle System Account selection change from native select
   */
  onSystemAccountSelectChange(): void {
    // Load local accounts based on selection
    this.loadLocalAccounts();
    this.cdr.detectChanges();
  }

  /**
   * Check if "All" is selected in System Account
   */
  isAllSystemAccountSelected(): boolean {
    return this.formState.systemAccount.length > 0 && this.formState.systemAccount[0]?.ACCT_KEY === 'All';
  }

  /**
   * Check if Local Account should be disabled
   * Original logic: disabled="(!(ctrl.showLocalAccountDrop && ctrl.systemAccount[0].ACCT_KEY !='all') || (ctrl.systemAccount.length === 0))"
   */
  isLocalAccountDisabled(): boolean {
    // Original: disabled="(!(ctrl.showLocalAccountDrop && ctrl.systemAccount[0].ACCT_KEY !='all') || (ctrl.systemAccount.length === 0))"
    // Disabled when:
    // 1. systemAccount is empty
    // 2. showLocalAccountDrop is false (data not loaded yet)
    // 3. First selected systemAccount is 'All' or 'all' (case insensitive)
    if (this.formState.systemAccount.length === 0) {
      return true;
    }
    if (!this.formState.showLocalAccountDrop) {
      return true;
    }
    const firstAcctKey = this.formState.systemAccount[0]?.ACCT_KEY;
    if (firstAcctKey === 'All' || firstAcctKey === 'all') {
      return true;
    }
    return false;
  }

  // ============================================
  // CUSTOM MULTISELECT - SYSTEM ACCOUNT
  // ============================================
  toggleSystemAccountDropdown(): void {
    this.systemAccountDropdownOpen = !this.systemAccountDropdownOpen;
    if (!this.systemAccountDropdownOpen) {
      this.systemAccountSearchText = '';
    }
    // Close local account dropdown
    this.localAccountDropdownOpen = false;
    this.cdr.detectChanges();
  }

  closeSystemAccountDropdown(): void {
    this.systemAccountDropdownOpen = false;
    this.systemAccountSearchText = '';
    this.cdr.detectChanges();
  }

  getFilteredSystemAccounts(): SystemAccountOption[] {
    if (!this.systemAccountSearchText) {
      return this.systemAccountDetails;
    }
    const search = this.systemAccountSearchText.toLowerCase();
    return this.systemAccountDetails.filter(item => 
      item.DESCRIPTION?.toLowerCase().includes(search)
    );
  }

  isSystemAccountSelected(option: SystemAccountOption): boolean {
    // Compare with type conversion for safety
    return this.formState.systemAccount.some(s => 
      s.ACCT_KEY === option.ACCT_KEY || String(s.ACCT_KEY) === String(option.ACCT_KEY)
    );
  }

  toggleSystemAccountSelection(option: SystemAccountOption): void {
    const index = this.formState.systemAccount.findIndex(s => 
      s.ACCT_KEY === option.ACCT_KEY || String(s.ACCT_KEY) === String(option.ACCT_KEY)
    );
    if (index > -1) {
      // Remove - using spread to create new array reference
      this.formState.systemAccount = this.formState.systemAccount.filter(s => 
        s.ACCT_KEY !== option.ACCT_KEY && String(s.ACCT_KEY) !== String(option.ACCT_KEY)
      );
    } else {
      // Add - using spread to create new array reference
      this.formState.systemAccount = [...this.formState.systemAccount, option];
    }
    this.onSystemAccountSelectChange();
    this.cdr.detectChanges();
  }

  selectAllSystemAccounts(): void {
    this.formState.systemAccount = [...this.getFilteredSystemAccounts()];
    this.onSystemAccountSelectChange();
    this.cdr.detectChanges();
  }

  unselectAllSystemAccounts(): void {
    this.formState.systemAccount = [];
    this.onSystemAccountSelectChange();
    this.cdr.detectChanges();
  }

  getSelectedSystemAccountsDisplay(): string {
    if (this.formState.systemAccount.length === 0) {
      return '';
    }
    if (this.formState.systemAccount.length === 1) {
      return this.formState.systemAccount[0].DESCRIPTION;
    }
    return `${this.formState.systemAccount.length} items selected`;
  }

  // ============================================
  // CUSTOM MULTISELECT - LOCAL ACCOUNT
  // ============================================
  toggleLocalAccountDropdown(): void {
    if (this.isLocalAccountDisabled()) {
      return;
    }
    this.localAccountDropdownOpen = !this.localAccountDropdownOpen;
    if (!this.localAccountDropdownOpen) {
      this.localAccountSearchText = '';
    }
    // Close system account dropdown
    this.systemAccountDropdownOpen = false;
    this.cdr.detectChanges();
  }

  closeLocalAccountDropdown(): void {
    this.localAccountDropdownOpen = false;
    this.localAccountSearchText = '';
    this.cdr.detectChanges();
  }

  getFilteredLocalAccounts(): LocalAccountOption[] {
    if (!this.localAccountSearchText) {
      return this.accountScheduleData;
    }
    const search = this.localAccountSearchText.toLowerCase();
    return this.accountScheduleData.filter(item => 
      item.ACCT_DESC?.toLowerCase().includes(search)
    );
  }

  isLocalAccountSelected(option: LocalAccountOption): boolean {
    // Compare with type conversion for safety
    return this.formState.localAccounts.some(l => 
      l.ACCT_KEY === option.ACCT_KEY || String(l.ACCT_KEY) === String(option.ACCT_KEY)
    );
  }

  toggleLocalAccountSelection(option: LocalAccountOption): void {
    const index = this.formState.localAccounts.findIndex(l => 
      l.ACCT_KEY === option.ACCT_KEY || String(l.ACCT_KEY) === String(option.ACCT_KEY)
    );
    if (index > -1) {
      this.formState.localAccounts = this.formState.localAccounts.filter(l => l.ACCT_KEY !== option.ACCT_KEY);
    } else {
      this.formState.localAccounts = [...this.formState.localAccounts, option];
    }
    this.cdr.detectChanges();
  }

  selectAllLocalAccounts(): void {
    this.formState.localAccounts = [...this.getFilteredLocalAccounts()];
    this.cdr.detectChanges();
  }

  unselectAllLocalAccounts(): void {
    this.formState.localAccounts = [];
    this.cdr.detectChanges();
  }

  getSelectedLocalAccountsDisplay(): string {
    if (this.formState.localAccounts.length === 0) {
      return '';
    }
    if (this.formState.localAccounts.length === 1) {
      return this.formState.localAccounts[0].ACCT_DESC;
    }
    return `${this.formState.localAccounts.length} items selected`;
  }

  // ============================================
  // CUSTOM MULTISELECT - SCHEDULE M
  // ============================================
  toggleScheduleMDropdown(): void {
    this.scheduleMDropdownOpen = !this.scheduleMDropdownOpen;
    if (!this.scheduleMDropdownOpen) {
      this.scheduleMSearchText = '';
    }
    // Close other dropdowns
    this.systemAccountDropdownOpen = false;
    this.localAccountDropdownOpen = false;
    this.cdr.detectChanges();
  }

  closeScheduleMDropdown(): void {
    this.scheduleMDropdownOpen = false;
    this.scheduleMSearchText = '';
    this.cdr.detectChanges();
  }

  getFilteredScheduleM(): ScheduleMOption[] {
    if (!this.scheduleMSearchText) {
      return this.scheduleMOptions;
    }
    const searchLower = this.scheduleMSearchText.toLowerCase();
    return this.scheduleMOptions.filter(option => 
      option.SCHDM_TEMPLATE_ID?.toString().toLowerCase().includes(searchLower) ||
      option.SCHDM_DESC?.toLowerCase().includes(searchLower)
    );
  }

  isScheduleMSelected(option: ScheduleMOption): boolean {
    return this.formState.scheduleMDetails.some(s => 
      s.SCHDM_TEMPLATE_ID === option.SCHDM_TEMPLATE_ID || 
      String(s.SCHDM_TEMPLATE_ID) === String(option.SCHDM_TEMPLATE_ID)
    );
  }

  toggleScheduleMSelection(option: ScheduleMOption): void {
    const index = this.formState.scheduleMDetails.findIndex(s => 
      s.SCHDM_TEMPLATE_ID === option.SCHDM_TEMPLATE_ID || 
      String(s.SCHDM_TEMPLATE_ID) === String(option.SCHDM_TEMPLATE_ID)
    );
    if (index > -1) {
      this.formState.scheduleMDetails = this.formState.scheduleMDetails.filter(s => 
        s.SCHDM_TEMPLATE_ID !== option.SCHDM_TEMPLATE_ID
      );
    } else {
      this.formState.scheduleMDetails = [...this.formState.scheduleMDetails, option];
    }
    this.cdr.detectChanges();
  }

  selectAllScheduleM(): void {
    this.formState.scheduleMDetails = [...this.getFilteredScheduleM()];
    this.cdr.detectChanges();
  }

  unselectAllScheduleM(): void {
    this.formState.scheduleMDetails = [];
    this.cdr.detectChanges();
  }

  getSelectedScheduleMDisplay(): string {
    if (this.formState.scheduleMDetails.length === 0) {
      return '';
    }
    if (this.formState.scheduleMDetails.length === 1) {
      return `${this.formState.scheduleMDetails[0].SCHDM_TEMPLATE_ID} - ${this.formState.scheduleMDetails[0].SCHDM_DESC}`;
    }
    return `${this.formState.scheduleMDetails.length} items selected`;
  }

  onBaseLevelChange(): void {
    const selectedLevel = this.baseLevelData.find(b => b.CODE_KEY === this.formState.selectedBaseLevelCode);
    if (selectedLevel?.CODE_DESCRIPTION === 'ALL ME' && this.baseTypeData.length !== this.formState.selectedBaseTypeCode.length) {
      this.formState.selectedBaseTypeCode = [...this.baseTypeData];
    }
    this.cdr.detectChanges();
  }

  private loadBaseType(): void {
    if (!this.formState.grossIncome?.DISP_ACCT_CODE) {
      console.log('[loadBaseType] Skipping - no grossIncome.DISP_ACCT_CODE');
      return;
    }

    // NOTE: In original AngularJS, getBaseType() (6xgttx) is called but its result is immediately
    // overwritten by getAllTransCatgExceptExp() (9aszao). The actual Base Type dropdown data
    // comes from allGPTransCategoryData (filtered transaction categories where GIE_NAME === 'GPROFIT').
    // So we skip the 6xgttx call and directly load the transaction categories.
    
    console.log('[loadBaseType] Loading transaction categories for Base Type dropdown');
    this.loadAllTransCategory();
  }

  private loadAllTransCategory(): void {
    const baseUrl = this.GENERAL_CONFIG.base_url;
    const scenario = this.globalService?.globalParams?.scenario ?? this.filterParams?.scenario;
    const jcdKey = this.globalService?.globalParams?.jcd_key ?? this.filterParams?.jcd_key;

    this.service.getAllTransCategory(baseUrl, scenario, jcdKey).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any[]) => {
        this.allTransCategoryData = data;
        this.allGPTransCategoryData = data.filter(d => d.GIE_NAME === 'GPROFIT');
        
        // IMPORTANT: Set baseTypeData from allGPTransCategoryData (matching AngularJS behavior)
        // The original AngularJS code overwrites baseTypeData with allGPTransCategoryData
        // When method is 'Gross Income', use allTransCategoryData; otherwise use allGPTransCategoryData
        if (this.formState.methodKey?.CODE_DESCRIPTION === 'Gross Income') {
          this.baseTypeData = [...this.allTransCategoryData];
        } else {
          this.baseTypeData = [...this.allGPTransCategoryData];
        }
        
        // Update selected values ONLY for initial load in create mode (not when user has already made selections)
        // This prevents overwriting user's Base Type selections when data reloads
        if (this.formState.isCreate && this.formState.selectedBaseTypeCode.length === 0) {
          this.formState.selectedBaseTypeCode = [...this.baseTypeData];
        }
        
        console.log('[loadAllTransCategory] baseTypeData updated:', this.baseTypeData.length, 'items');
        
        // Update visibility after baseTypeData is properly set
        this.updateFormVisibility();
        this.cdr.detectChanges();
      },
      error => {
        console.error('Error loading all trans category:', error);
      }
    );
  }

  /**
   * Load all trans category data for EDIT mode
   * This ensures baseTypeData is populated so Base Type control becomes visible
   */
  private loadAllTransCategoryForEdit(): void {
    const baseUrl = this.GENERAL_CONFIG.base_url;
    const scenario = this.globalService?.globalParams?.scenario ?? this.filterParams?.scenario;
    const jcdKey = this.globalService?.globalParams?.jcd_key ?? this.filterParams?.jcd_key;

    this.service.getAllTransCategory(baseUrl, scenario, jcdKey).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any[]) => {
        this.allTransCategoryData = data;
        this.allGPTransCategoryData = data.filter(d => d.GIE_NAME === 'GPROFIT');
        
        // Set baseTypeData based on method (matching AngularJS editbaseType logic)
        if (this.formState.methodKey?.CODE_DESCRIPTION === 'Gross Income') {
          this.baseTypeData = [...this.allTransCategoryData];
        } else {
          this.baseTypeData = [...this.allGPTransCategoryData];
        }
        
        console.log('[loadAllTransCategoryForEdit] baseTypeData populated:', this.baseTypeData.length, 'items');
        
        // For EDIT mode: if BASE_TYPE is 'ALL', set all types as selected
        if (this.rowData?.BASE_TYPE === 'ALL') {
          this.formState.selectedBaseTypeCode = [...this.baseTypeData];
          console.log('[loadAllTransCategoryForEdit] Base Type set to ALL:', this.baseTypeData.length, 'items');
        }
        
        // Update visibility after baseTypeData is properly set
        this.updateFormVisibility();
        this.cdr.detectChanges();
      },
      error => {
        console.error('Error loading all trans category for edit:', error);
      }
    );
  }

  // ============================================
  // CUSTOM MULTISELECT - BASE FDII
  // ============================================
  toggleBaseFdiiDropdown(): void {
    this.baseFdiiDropdownOpen = !this.baseFdiiDropdownOpen;
    if (!this.baseFdiiDropdownOpen) {
      this.baseFdiiSearchText = '';
    }
    // Close other dropdowns
    this.baseBasketDropdownOpen = false;
    this.baseTypeDropdownOpen = false;
    this.systemAccountDropdownOpen = false;
    this.localAccountDropdownOpen = false;
    this.scheduleMDropdownOpen = false;
    this.cdr.detectChanges();
  }

  getFilteredBaseFdiiOptions(): BaseCodeOption[] {
    if (!this.baseFdiiSearchText) {
      return this.baseFDIIData;
    }
    const searchLower = this.baseFdiiSearchText.toLowerCase();
    return this.baseFDIIData.filter(option =>
      option.CODE_DESCRIPTION?.toLowerCase().includes(searchLower)
    );
  }

  isBaseFdiiSelected(option: BaseCodeOption): boolean {
    if (!Array.isArray(this.formState.selectedBaseFdiiCodes)) {
      return false;
    }
    return this.formState.selectedBaseFdiiCodes.some((s: any) => s.CODE_KEY === option.CODE_KEY);
  }

  toggleBaseFdiiSelection(option: BaseCodeOption, event?: Event): void {
    // Ensure we have an array (not string from Allocate mode)
    if (!Array.isArray(this.formState.selectedBaseFdiiCodes)) {
      this.formState.selectedBaseFdiiCodes = [];
    }
    const isSelected = this.formState.selectedBaseFdiiCodes.some((s: any) => s.CODE_KEY === option.CODE_KEY);
    if (isSelected) {
      // Remove - using filter to create new array reference (immutable)
      this.formState.selectedBaseFdiiCodes = this.formState.selectedBaseFdiiCodes.filter(
        (s: any) => s.CODE_KEY !== option.CODE_KEY
      );
    } else {
      // Add - using spread to create new array reference (immutable)
      this.formState.selectedBaseFdiiCodes = [...this.formState.selectedBaseFdiiCodes, option];
    }
    this.cdr.detectChanges();
  }

  selectAllBaseFdii(event?: Event): void {
    this.formState.selectedBaseFdiiCodes = [...this.baseFDIIData];
    this.cdr.detectChanges();
  }

  unselectAllBaseFdii(event?: Event): void {
    this.formState.selectedBaseFdiiCodes = [];
    this.cdr.detectChanges();
  }

  getSelectedBaseFdiiDisplay(): string {
    if (!Array.isArray(this.formState.selectedBaseFdiiCodes) || this.formState.selectedBaseFdiiCodes.length === 0) {
      return '';
    }
    if (this.formState.selectedBaseFdiiCodes.length === 1) {
      return this.formState.selectedBaseFdiiCodes[0].CODE_DESCRIPTION;
    }
    return `${this.formState.selectedBaseFdiiCodes.length} items selected`;
  }

  // ============================================
  // CUSTOM MULTISELECT - BASE BASKET
  // ============================================
  toggleBaseBasketDropdown(): void {
    this.baseBasketDropdownOpen = !this.baseBasketDropdownOpen;
    if (!this.baseBasketDropdownOpen) {
      this.baseBasketSearchText = '';
    }
    // Close other dropdowns
    this.baseFdiiDropdownOpen = false;
    this.baseTypeDropdownOpen = false;
    this.systemAccountDropdownOpen = false;
    this.localAccountDropdownOpen = false;
    this.scheduleMDropdownOpen = false;
    this.cdr.detectChanges();
  }

  getFilteredBaseBasketOptions(): BaseCodeOption[] {
    if (!this.baseBasketSearchText) {
      return this.baseBasketData;
    }
    const searchLower = this.baseBasketSearchText.toLowerCase();
    return this.baseBasketData.filter(option =>
      option.CODE_DESCRIPTION?.toLowerCase().includes(searchLower)
    );
  }

  isBaseBasketSelected(option: BaseCodeOption): boolean {
    if (!Array.isArray(this.formState.selectedBaseBasketCode)) {
      return false;
    }
    return this.formState.selectedBaseBasketCode.some((s: any) => s.CODE_KEY === option.CODE_KEY);
  }

  toggleBaseBasketSelection(option: BaseCodeOption, event?: Event): void {
    // Ensure we have an array (not string from Allocate mode)
    if (!Array.isArray(this.formState.selectedBaseBasketCode)) {
      this.formState.selectedBaseBasketCode = [];
    }
    const isSelected = this.formState.selectedBaseBasketCode.some((s: any) => s.CODE_KEY === option.CODE_KEY);
    if (isSelected) {
      // Remove - using filter to create new array reference (immutable)
      this.formState.selectedBaseBasketCode = this.formState.selectedBaseBasketCode.filter(
        (s: any) => s.CODE_KEY !== option.CODE_KEY
      );
    } else {
      // Add - using spread to create new array reference (immutable)
      this.formState.selectedBaseBasketCode = [...this.formState.selectedBaseBasketCode, option];
    }
    this.cdr.detectChanges();
  }

  selectAllBaseBasket(event?: Event): void {
    this.formState.selectedBaseBasketCode = [...this.baseBasketData];
    this.cdr.detectChanges();
  }

  unselectAllBaseBasket(event?: Event): void {
    this.formState.selectedBaseBasketCode = [];
    this.cdr.detectChanges();
  }

  getSelectedBaseBasketDisplay(): string {
    if (!Array.isArray(this.formState.selectedBaseBasketCode) || this.formState.selectedBaseBasketCode.length === 0) {
      return '';
    }
    if (this.formState.selectedBaseBasketCode.length === 1) {
      return this.formState.selectedBaseBasketCode[0].CODE_DESCRIPTION;
    }
    return `${this.formState.selectedBaseBasketCode.length} items selected`;
  }

  // ============================================
  // CUSTOM MULTISELECT - BASE TYPE
  // ============================================
  toggleBaseTypeDropdown(): void {
    this.baseTypeDropdownOpen = !this.baseTypeDropdownOpen;
    if (!this.baseTypeDropdownOpen) {
      this.baseTypeSearchText = '';
    }
    // Close other dropdowns
    this.baseFdiiDropdownOpen = false;
    this.baseBasketDropdownOpen = false;
    this.systemAccountDropdownOpen = false;
    this.localAccountDropdownOpen = false;
    this.scheduleMDropdownOpen = false;
    this.cdr.detectChanges();
  }

  getFilteredBaseTypeOptions(): BaseCodeOption[] {
    if (!this.baseTypeSearchText) {
      return this.baseTypeData;
    }
    const searchLower = this.baseTypeSearchText.toLowerCase();
    return this.baseTypeData.filter(option =>
      option.CODE_DESCRIPTION?.toLowerCase().includes(searchLower) ||
      option.CODE_UPDATED_DESCRIPTION?.toLowerCase().includes(searchLower)
    );
  }

  isBaseTypeSelected(option: BaseCodeOption): boolean {
    if (!Array.isArray(this.formState.selectedBaseTypeCode)) {
      return false;
    }
    return this.formState.selectedBaseTypeCode.some((s: any) => s.CODE_KEY === option.CODE_KEY);
  }

  toggleBaseTypeSelection(option: BaseCodeOption, event?: Event): void {
    // Ensure we have an array
    if (!Array.isArray(this.formState.selectedBaseTypeCode)) {
      this.formState.selectedBaseTypeCode = [];
    }
    const isSelected = this.formState.selectedBaseTypeCode.some((s: any) => s.CODE_KEY === option.CODE_KEY);
    if (isSelected) {
      // Remove - using filter to create new array reference (immutable)
      this.formState.selectedBaseTypeCode = this.formState.selectedBaseTypeCode.filter(
        (s: any) => s.CODE_KEY !== option.CODE_KEY
      );
    } else {
      // Add - using spread to create new array reference (immutable)
      this.formState.selectedBaseTypeCode = [...this.formState.selectedBaseTypeCode, option];
    }
    this.cdr.detectChanges();
  }

  selectAllBaseType(event?: Event): void {
    this.formState.selectedBaseTypeCode = [...this.baseTypeData];
    this.cdr.detectChanges();
  }

  unselectAllBaseType(event?: Event): void {
    this.formState.selectedBaseTypeCode = [];
    this.cdr.detectChanges();
  }

  getSelectedBaseTypeDisplay(): string {
    if (!Array.isArray(this.formState.selectedBaseTypeCode) || this.formState.selectedBaseTypeCode.length === 0) {
      return '';
    }
    if (this.formState.selectedBaseTypeCode.length === 1) {
      const option = this.formState.selectedBaseTypeCode[0];
      return this.formState.methodKey?.CODE_DESCRIPTION === 'Gross Income' 
        ? (option.CODE_UPDATED_DESCRIPTION || option.CODE_DESCRIPTION || '')
        : (option.CODE_DESCRIPTION || '');
    }
    return `${this.formState.selectedBaseTypeCode.length} items selected`;
  }

  // ============================================
  // VALIDATION
  // ============================================
  private validateForm(): boolean {
    const state = this.formState;

    if (!state.level?.CODE_DESCRIPTION) {
      this.service.openSnackBar('Please select Level', 'error-snackbar');
      return false;
    }

    // Check level entity based on selected level
    if (state.level.CODE_DESCRIPTION === 'Parent ME' && !state.parentME?.PARENT_ME_KEY) {
      this.service.openSnackBar('Please select required fields', 'error-snackbar');
      return false;
    }

    if (!state.grossIncome?.ACCT_KEY) {
      this.service.openSnackBar('Please select required fields', 'error-snackbar');
      return false;
    }

    if (!state.transactionCategory?.trim()) {
      this.service.openSnackBar('Please provide Transaction Category', 'error-snackbar');
      return false;
    }

    if (!state.mapTo?.CODE_DESCRIPTION) {
      this.service.openSnackBar('Please select Map', 'error-snackbar');
      return false;
    }

    // Validate by Account fields
    if (state.mapTo.CODE_DESCRIPTION === 'by Account') {
      if (state.systemAccount.length === 0) {
        this.service.openSnackBar('Please select System Account', 'error-snackbar');
        return false;
      }

      if (state.percentage === undefined || state.percentage < 1 || state.percentage > 100) {
        this.service.openSnackBar('Please provide percentage between 1 to 100', 'error-snackbar');
        return false;
      }

      // Validate Local Account - required when System Account is not "All" and grossIncome is not "Asset Exp"
      const isSystemAccountAll = state.systemAccount.length === 1 && state.systemAccount[0]?.ACCT_KEY === 'All';
      const isAssetExp = state.grossIncome?.DISP_ACCT_CODE === 'Asset Exp';
      
      if (!isSystemAccountAll && !isAssetExp && state.localAccounts.length === 0) {
        this.service.openSnackBar('Please select Local Account', 'error-snackbar');
        return false;
      }

      // Validate that "All" is not selected together with other options
      const hasAllInSystemAccount = state.systemAccount.some(s => s?.ACCT_KEY === 'All');
      if (hasAllInSystemAccount && state.systemAccount.length > 1) {
        this.service.openSnackBar('Please select either ALL or specific System Accounts', 'error-snackbar');
        return false;
      }

      const hasAllInLocalAccount = state.localAccounts.some(l => l?.ACCT_KEY === 'All');
      if (hasAllInLocalAccount && state.localAccounts.length > 1) {
        this.service.openSnackBar('Please select either ALL or specific Local Accounts', 'error-snackbar');
        return false;
      }
    }

    // Validate by Schedule M fields
    if (state.mapTo.CODE_DESCRIPTION === 'by Schedule M' && state.scheduleMDetails.length === 0) {
      this.service.openSnackBar('Please select Schedule M', 'error-snackbar');
      return false;
    }

    // Validate method for Interest Expense / Research
    if ((state.grossIncome.DISP_ACCT_CODE === 'Research' || state.grossIncome.DISP_ACCT_CODE === 'Interest Expense') &&
        state.mapTo.CODE_DESCRIPTION !== 'by Amount' && !state.methodKey?.CODE_KEY) {
      this.service.openSnackBar('Please select Method Desc', 'error-snackbar');
      return false;
    }

    return true;
  }

  // ============================================
  // SAVE / RESET / CANCEL
  // ============================================
  save(): void {
    if (this.formState.crudLoading) {
      this.service.openSnackBar('Please wait while we save this request', 'info-snackbar');
      return;
    }

    if (!this.validateForm()) {
      this.formState.isSaveClicked = false;
      return;
    }

    this.formState.isSaveClicked = true;
    this.formState.crudLoading = true;
    this.cdr.detectChanges();

    const payload = this.buildSavePayload(false);
    this.performSave(payload);
  }

  delete(): void {
    if (this.deleteLoading) {
      return;
    }

    // Show confirmation modal before deleting
    this.showDeleteModal = true;
    this.cdr.detectChanges();
  }

  onDeleteConfirm(): void {
    // Called from confirmation modal - proceed with delete
    this.showDeleteModal = false;
    this.performDelete();
  }

  onDeleteCancel(): void {
    // Called from confirmation modal - close modal
    this.showDeleteModal = false;
    this.cdr.detectChanges();
  }

  private performDelete(): void {
    this.deleteLoading = true;
    this.cdr.detectChanges();

    const payload = this.buildSavePayload(true);
    const baseUrl = this.GENERAL_CONFIG.base_url;
    const ssoId = this.globalService?.userSettingsData?.user?.sso_id || '';
    const screenKey = this.globalService?.globalParams?.screen_key || 0;
    const taxYear = this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year;
    const scenario = this.filterParams?.scenario ?? this.globalService?.globalParams?.scenario;
    const issueParams = this.gtwGlobal.isIssueIDEnabled(taxYear, scenario);
    const scenarioCode = this.filterParams?.scenarioCode
      || this.globalService?.globalParams?.scenarioCode
      || issueParams.scenarioCode;
    const isIssueKeyEnabled = this.filterParams?.is_issue_key_enabled
      || this.globalService?.globalParams?.is_issue_key_enabled
      || issueParams.is_issue_key_enabled;
    
    this.service.saveTransCategory(
      baseUrl,
      payload,
      { 
        sso_id: ssoId, 
        screen_key: screenKey,
        scenarioCode: scenarioCode,
        is_issue_key_enabled: isIssueKeyEnabled
      }
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.deleteLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          // Show API error message if available, otherwise generic message
          this.service.openSnackBar(response.errorMessage, 'error-snackbar');
        } else {
          this.service.openSnackBar('Trans Category deleted successfully', 'success-snackbar');
          // Emit refresh event to update grid (same as save)
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.deleteLoading = false;
        // Show error message from response if available
        const errorMsg = error?.error?.errorMessage || error?.message || 'Error occurred while deleting. Please try again.';
        this.service.openSnackBar(errorMsg, 'error-snackbar');
        this.cdr.detectChanges();
      }
    );
  }

  private buildSavePayload(isDelete: boolean): SaveTransCategoryPayload {
    const state = this.formState;

    const payload: SaveTransCategoryPayload = {
      type: isDelete ? 'D' : (state.isCreate ? 'I' : 'U'),
      trans_catg_key: state.isCreate ? null : (this.rowData.TRANS_CATG_KEY || null),
      trans_catg_name: state.transactionCategory,
      gross_acct_key: state.grossIncome!.ACCT_KEY,
      level: state.level!.CODE_KEY,
      map_key: state.mapTo!.CODE_KEY,
      GIE_KEY: state.grossIncome?.GIE_KEY,
      is_core: 'N',
      is_ftc: 'Y',
      is_changed: (!state.isCreate && state.isChanged) ? 'Y' : 'N'
    };

    // Set ME key based on level
    if (state.level?.CODE_DESCRIPTION === 'Parent ME' && state.parentME) {
      payload.me_key = state.parentME.PARENT_ME_KEY;
    } else if (state.level?.CODE_DESCRIPTION === 'Filing Group' && state.filingGroup) {
      payload.me_key = state.filingGroup.PARENT_ME_KEY;
    } else if (state.level?.CODE_DESCRIPTION === 'ME' && state.meInfo) {
      payload.me_key = state.meInfo.PARENT_ME_KEY;
    }

    // Set method key (only if selected, don't include null - matches original behavior)
    if (state.methodKey?.CODE_KEY && state.methodKey.CODE_KEY !== 0) {
      payload.method_key = state.methodKey.CODE_KEY;
    }

    // Handle by Amount
    if (state.mapTo?.CODE_DESCRIPTION === 'by Amount') {
      payload.is_all_sys_acct = null;
      payload.sys_acct_key = null;
      payload.is_all_local_acct = null;
      payload.local_acct_key = null;
      payload.is_all_m_temp = null;
      payload.m_template_key = null;
    }

    // Handle by Schedule M
    if (state.mapTo?.CODE_DESCRIPTION === 'by Schedule M') {
      const templateIds = state.scheduleMDetails.map(s => s.SCHDM_TEMPLATE_ID).join(',');
      payload.is_all_m_temp = state.scheduleMDetails.length > 0 && state.scheduleMDetails[0].SCHDM_TEMPLATE_ID !== 'All' ? 'N' : 'Y';
      payload.m_template_key = payload.is_all_m_temp === 'N' ? templateIds : null;
    }

    // Handle by Account
    if (state.mapTo?.CODE_DESCRIPTION === 'by Account') {
      payload.percentage = state.percentage > 100 ? 100 : state.percentage;

      const isAllSysAcct = state.systemAccount.length > 0 && state.systemAccount[0].ACCT_KEY === 'All';
      payload.is_all_sys_acct = isAllSysAcct ? 'Y' : 'N';

      if (isAllSysAcct) {
        payload.sys_acct_key = null;
        payload.is_all_local_acct = null;
        payload.local_acct_key = null;
      } else {
        const isAllLocalAcct = state.localAccounts.length > 0 && state.localAccounts[0].ACCT_KEY === 'All';
        payload.is_all_local_acct = isAllLocalAcct ? 'Y' : 'N';

        if (!isAllLocalAcct && state.localAccounts.length > 0) {
          payload.local_acct_key = state.localAccounts.map(l => l.ACCT_KEY).join(',');
        }

        if (!isAllSysAcct && state.systemAccount.length > 0) {
          payload.sys_acct_key = state.systemAccount.map(s => s.ACCT_KEY).join(',');
        }
      }
    }

    // Build attrib_value
    const attribValues: string[] = [];

    if (state.methodKey?.CODE_KEY && state.mapTo?.CODE_DESCRIPTION !== 'by Amount') {
      const methodAttrib = this.attribMastData.find(a => a.ATTRIB_NAME === 'METHOD');
      if (methodAttrib) {
        attribValues.push(`${methodAttrib.ATTRIB_KEY}_${state.methodKey.CODE_KEY}`);
      }
    }

    // Handle base fields for multi-select
    if (state.newArray.includes('selectedBaseFdiiCodes') && Array.isArray(state.selectedBaseFdiiCodes)) {
      const baseFdiiCodes = (state.selectedBaseFdiiCodes as BaseCodeOption[]).map(b => b.CODE_KEY).join(',');
      payload.BASE_FDII_DATA = baseFdiiCodes;

      const fdiiAttrib = this.attribMastData.find(a => a.ATTRIB_NAME === 'BASE_FDII');
      if (fdiiAttrib) {
        if ((state.selectedBaseFdiiCodes as BaseCodeOption[]).length === this.baseFDIIData.length) {
          const allCode = this.basefdiiSingleMultiData.find(b => b.CODE_DESC === 'ALL');
          if (allCode) {
            attribValues.push(`${fdiiAttrib.ATTRIB_KEY}_${allCode.CODE_KEY}`);
            payload.BASE_FDII_DATA = null;
          }
        } else if ((state.selectedBaseFdiiCodes as BaseCodeOption[]).length === 1) {
          const singleCode = this.basefdiiSingleMultiData.find(b => b.CODE_DESC === 'SINGLE');
          if (singleCode) {
            attribValues.push(`${fdiiAttrib.ATTRIB_KEY}_${singleCode.CODE_KEY}`);
          }
        } else {
          const multiCode = this.basefdiiSingleMultiData.find(b => b.CODE_DESC === 'MULTI');
          if (multiCode) {
            attribValues.push(`${fdiiAttrib.ATTRIB_KEY}_${multiCode.CODE_KEY}`);
          }
        }
      }
    }

    // Handle base fields for single-select (Allocate method)
    if (state.newArray.includes('selectedBaseFdiiCodes_single') && typeof state.selectedBaseFdiiCodes === 'string') {
      payload.BASE_FDII_DATA = state.selectedBaseFdiiCodes;
      const fdiiAttrib = this.attribMastData.find(a => a.ATTRIB_NAME === 'BASE_FDII');
      const singleCode = this.basefdiiSingleMultiData.find(b => b.CODE_DESC === 'SINGLE');
      if (fdiiAttrib && singleCode) {
        attribValues.push(`${fdiiAttrib.ATTRIB_KEY}_${singleCode.CODE_KEY}`);
      }
    }

    // Handle Base Basket for multi-select (non-Allocate method)
    if (state.newArray.includes('selectedBaseBasketCode') && Array.isArray(state.selectedBaseBasketCode)) {
      const baseBasketCodes = (state.selectedBaseBasketCode as BaseCodeOption[]).map(b => b.CODE_KEY).join(',');
      payload.BASE_BASKET_DATA = baseBasketCodes;

      const basketAttrib = this.attribMastData.find(a => a.ATTRIB_NAME === 'BASE_BASKET');
      if (basketAttrib) {
        if ((state.selectedBaseBasketCode as BaseCodeOption[]).length === this.baseBasketData.length) {
          // All selected
          const allCode = this.baseBasketTypeData.find(b => b.CODE_DESCRIPTION === 'ALL');
          if (allCode) {
            attribValues.push(`${basketAttrib.ATTRIB_KEY}_${allCode.CODE_KEY}`);
            payload.BASE_BASKET_DATA = null;
          }
        } else if ((state.selectedBaseBasketCode as BaseCodeOption[]).length === 1) {
          // Single selected
          const singleCode = this.baseBasketTypeData.find(b => b.CODE_DESCRIPTION === 'SINGLE');
          if (singleCode) {
            attribValues.push(`${basketAttrib.ATTRIB_KEY}_${singleCode.CODE_KEY}`);
          }
        } else {
          // Multi selected
          const multiCode = this.baseBasketTypeData.find(b => b.CODE_DESCRIPTION === 'MULTI');
          if (multiCode) {
            attribValues.push(`${basketAttrib.ATTRIB_KEY}_${multiCode.CODE_KEY}`);
          }
        }
      }
    }

    // Handle Base Basket for single-select (Allocate method)
    if (state.newArray.includes('selectedBaseBasketCode__single') && typeof state.selectedBaseBasketCode === 'string') {
      payload.BASE_BASKET_DATA = state.selectedBaseBasketCode;
      const basketAttrib = this.attribMastData.find(a => a.ATTRIB_NAME === 'BASE_BASKET');
      const singleCode = this.baseBasketTypeData.find(b => b.CODE_DESCRIPTION === 'SINGLE');
      if (basketAttrib && singleCode) {
        attribValues.push(`${basketAttrib.ATTRIB_KEY}_${singleCode.CODE_KEY}`);
      }
    }

    // Handle Base Type for multi-select
    if (state.newArray.includes('selectedBaseTypeCode') && Array.isArray(state.selectedBaseTypeCode)) {
      const baseTypeCodes = (state.selectedBaseTypeCode as any[]).map(b => b.CODE_KEY || b.GIE_KEY).join(',');
      payload.BASE_TYPE_DATA = baseTypeCodes;

      const typeAttrib = this.attribMastData.find(a => a.ATTRIB_NAME === 'BASE_TYPE');
      if (typeAttrib) {
        if ((state.selectedBaseTypeCode as any[]).length === this.baseTypeData.length) {
          // All selected
          const allCode = this.baseTypeCodeData.find(b => b.CODE_DESCRIPTION === 'ALL');
          if (allCode) {
            attribValues.push(`${typeAttrib.ATTRIB_KEY}_${allCode.CODE_KEY}`);
            payload.BASE_TYPE_DATA = null;
          }
        } else if ((state.selectedBaseTypeCode as any[]).length === 1) {
          // Single selected
          const singleCode = this.baseTypeCodeData.find(b => b.CODE_DESCRIPTION === 'SINGLE');
          if (singleCode) {
            attribValues.push(`${typeAttrib.ATTRIB_KEY}_${singleCode.CODE_KEY}`);
          }
        } else {
          // Multi selected
          const multiCode = this.baseTypeCodeData.find(b => b.CODE_DESCRIPTION === 'MULTI');
          if (multiCode) {
            attribValues.push(`${typeAttrib.ATTRIB_KEY}_${multiCode.CODE_KEY}`);
          }
        }
      }
    }

    if (state.selectedBaseLevelCode && state.newArray.includes('selectedBaseLevelCode')) {
      const levelAttrib = this.attribMastData.find(a => a.ATTRIB_NAME === 'BASE_LEVEL');
      if (levelAttrib) {
        attribValues.push(`${levelAttrib.ATTRIB_KEY}_${state.selectedBaseLevelCode}`);
      }
    }

    if (state.selectedBaseEntityLevelCode && state.newArray.includes('selectedBaseEntityLevelCode')) {
      const entityAttrib = this.attribMastData.find(a => a.ATTRIB_NAME === 'BASE_ENTITY_LEVEL');
      if (entityAttrib) {
        attribValues.push(`${entityAttrib.ATTRIB_KEY}_${state.selectedBaseEntityLevelCode}`);
      }
    }

    payload.attrib_value = attribValues.join(',');

    return payload;
  }

  private performSave(payload: SaveTransCategoryPayload): void {
    const baseUrl = this.GENERAL_CONFIG.base_url;
    const ssoId = this.globalService?.userSettingsData?.user?.sso_id || '';
    const screenKey = this.globalService?.globalParams?.screen_key || 0;
    const taxYear = this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year;
    const scenario = this.filterParams?.scenario ?? this.globalService?.globalParams?.scenario;
    const issueParams = this.gtwGlobal.isIssueIDEnabled(taxYear, scenario);
    const scenarioCode = this.filterParams?.scenarioCode
      || this.globalService?.globalParams?.scenarioCode
      || issueParams.scenarioCode;
    const isIssueKeyEnabled = this.filterParams?.is_issue_key_enabled
      || this.globalService?.globalParams?.is_issue_key_enabled
      || issueParams.is_issue_key_enabled;

    this.service.saveTransCategory(
      baseUrl,
      payload,
      { 
        sso_id: ssoId, 
        screen_key: screenKey,
        scenarioCode: scenarioCode,
        is_issue_key_enabled: isIssueKeyEnabled
      }
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.formState.isSaveClicked = false;
        this.formState.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          const errorKey = this.extractErrorKey(response.errorMessage);
          if (errorKey === 'GTWDBA01.IND1_FTC_TRANS_CATG_DETAILS') {
            this.service.openSnackBar('Trans Category already exists', 'error-snackbar');
          } else {
            this.service.openSnackBar(response.errorMessage, 'error-snackbar');
          }
        } else {
          this.service.openSnackBar('Transaction Category has been successfully saved/updated', 'success-snackbar');
          this.requestGridUpdate.emit('refresh');
        }

        this.cdr.detectChanges();
      },
      (error: any) => {
        this.formState.isSaveClicked = false;
        this.formState.crudLoading = false;
        this.service.openSnackBar('Error occurred while saving. Please try again.', 'error-snackbar');
        this.cdr.detectChanges();
      }
    );
  }

  private extractErrorKey(errorMessage: string): string {
    const startIndex = errorMessage.indexOf('(') + 1;
    const lastIndex = errorMessage.indexOf(')');
    return errorMessage.substring(startIndex, lastIndex);
  }

  reset(): void {
    // Reset method tracking flags
    this.isBaseFieldsInitialized = false;
    this.previousMethodDescription = null;
    
    if (this.formState.isCreate) {
      // Reset all fields to initial state for ADD mode (matching AngularJS resetAdd)
      this.formState.level = null;
      this.formState.level1Show = false;
      this.formState.parentME = null;
      this.formState.filingGroup = null;
      this.formState.meInfo = null;
      this.formState.grossIncome = null;
      this.formState.transactionCategory = '';
      this.formState.mapTo = null;
      this.formState.isINC = false;
      this.formState.level2Show = false;
      this.formState.percentage = 100;
      this.formState.systemAccount = [];
      this.formState.localAccounts = [];
      this.formState.scheduleMDetails = [];
      this.formState.methodKey = null;
      this.formState.selectedBaseFdiiCodes = [];
      this.formState.selectedBaseBasketCode = [];
      this.formState.selectedBaseTypeCode = [];
      this.formState.selectedBaseLevelCode = null;
      this.formState.selectedBaseEntityLevelCode = null;
      this.formState.isSaveClicked = false;
      
      // Reset visibility flags
      this.formState.showMethodDesc = false;
      this.formState.showBaseFdii = false;
      this.formState.showBaseBasket = false;
      this.formState.showBaseType = false;
      this.formState.showBaseLevel = false;
      this.formState.showBaseEntityLevel = false;
      this.formState.showBaseFdiiSingle = false;
      this.formState.showBaseBasketSingle = false;
      this.formState.showSystemAccount = false;
      this.formState.showLocalAccount = false;
      this.formState.showPercentage = false;
      this.formState.showScheduleM = false;
      this.formState.showLocalAccountDrop = false;
      
      // Reset data arrays
      this.systemAccountDetails = [];
      this.accountScheduleData = [];
      this.baseTypeData = [];
    } else {
      // Reset to original values for EDIT mode (matching AngularJS resetEdit)
      this.formState.isLoading = true;
      this.formState.systemAccount = [];
      this.formState.localAccounts = [];
      this.formState.scheduleMDetails = [];
      this.formState.percentage = this.rowData.PERCENTAGE || 100;
      this.formState.transactionCategory = this.rowData.TRANS_CATG_NAME || '';
      this.formState.isSaveClicked = false;
      this.formState.isChanged = false;
      
      // Reset data arrays
      this.systemAccountDetails = [];
      this.accountScheduleData = [];
      
      // Reload data from rowData
      this.loadInitialData();
    }
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  // ============================================
  // HELPER METHODS
  // ============================================
  getLevelEntityLabel(): string {
    return this.formState.level?.CODE_DESCRIPTION || '';
  }

  isBaseLevelAllME(): boolean {
    if (!this.formState.selectedBaseLevelCode) {
      return false;
    }
    const selectedLevel = this.baseLevelData.find(b => b.CODE_KEY === this.formState.selectedBaseLevelCode);
    return selectedLevel?.CODE_DESCRIPTION === 'ALL ME';
  }

  trackByFn(index: number, item: any): any {
    return item?.CODE_KEY || item?.ACCT_KEY || item?.SCHDM_TEMPLATE_ID || index;
  }
}
